import { Link, router } from '@inertiajs/react'
import { Star, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/Select'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import type { HelpArticleRow, HelpCategoryNode } from '@/pages/HelpCenter/types'

interface HelpArticleTableProps {
  articles: HelpArticleRow[]
  categories: HelpCategoryNode[]
  categoryId: string
  onCategoryChange: (categoryId: string) => void
}

export function HelpArticleTable({ articles, categories, categoryId, onCategoryChange }: HelpArticleTableProps) {
  const { withAccount } = useRouteParams()
  const [query, setQuery] = useState('')
  const [status, setStatus] = useState('all')

  const categoryName = (id: string | null) => categories.find(c => c.id === id)?.name

  const rows = useMemo(() => articles.filter(a => {
    if (status === 'published' && !a.published) return false
    if (status === 'draft' && a.published) return false
    if (categoryId !== 'all' && a.help_category_id !== categoryId) return false
    if (query && !a.title.toLowerCase().includes(query.toLowerCase())) return false
    return true
  }), [articles, status, categoryId, query])

  const remove = (article: HelpArticleRow) => {
    router.delete(routes.helpArticles.destroy.path(withAccount({ id: article.slug })), { preserveScroll: true })
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Filter articles…" className="max-w-xs" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
          </SelectContent>
        </Select>
        <Select value={categoryId} onValueChange={onCategoryChange}>
          <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-lg border border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Article</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Views</TableHead>
              <TableHead className="text-right">Helpful</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                  No matching articles.
                </TableCell>
              </TableRow>
            ) : rows.map(article => (
              <TableRow key={article.id} className="cursor-pointer">
                <TableCell>
                  <Link href={routes.helpArticles.edit.path(withAccount({ id: article.slug }))} className="flex items-center gap-1.5 font-medium hover:underline">
                    {article.featured && <Star className="size-3.5 fill-primary text-primary" />}
                    {article.title}
                  </Link>
                </TableCell>
                <TableCell className="text-muted-foreground">{categoryName(article.help_category_id) ?? '—'}</TableCell>
                <TableCell>
                  <Badge variant={article.published ? 'default' : 'secondary'}>{article.published ? 'Published' : 'Draft'}</Badge>
                </TableCell>
                <TableCell className="text-right tabular-nums">{article.views_count}</TableCell>
                <TableCell className="text-right tabular-nums">{article.helpfulness_score}%</TableCell>
                <TableCell>
                  <ConfirmDialog
                    trigger={<Button variant="ghost" size="icon" className="size-7" aria-label="Delete article"><Trash2 className="size-3.5" /></Button>}
                    title={`Delete "${article.title}"?`}
                    description="This permanently removes the article. This can't be undone."
                    confirmText="Delete"
                    destructive
                    onConfirm={() => remove(article)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
